import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@tailoring-crm/ui';

/**
 * What a screen shows when there is nothing on it.
 *
 * An empty list is the first thing a new shop sees, so it must say what this screen is FOR and
 * offer the one action that fills it — never a bare "No data". The distinction between "you
 * have not added any customers yet" and "no customer matches this search" is the caller's to
 * make; both come through here with different copy.
 *
 * All strings arrive already translated.
 */
export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  /** The one thing to do next. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-6 py-12 text-center',
        className,
      )}
    >
      {Icon ? (
        <span className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon aria-hidden="true" className="size-6" />
        </span>
      ) : null}
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}
